import { useOutletContext } from "react-router-dom";
import { RiderModel, RiderProductModel } from "../../Api/Models/rider.model";
import { riderService } from "../../Api/Services/RiderService";
import { useLoading } from "../../Context/LoadingProvider";
import React, { useEffect, useState } from "react";
import { RiderProfileLayoutContextInterface } from "../../Pages/Rider/RiderProfileLayout";
import ImageInput from "../ImageInput";
import { t } from "i18next";
import { useInfoModal } from "../../Context/InfoModalProvider";
import { stockService } from "../../Api/Services/StockService";
import { StockModel } from "../../Api/Models/stock.model";
import ExchangeTshirts from "./ExchangeTshirts";
import {
  KitModel,
  KitProductModel,
  ProductModel,
} from "../../Api/Models/product.model";
import ExchangeMissing from "./ExchangeMissing";
import ProductState from "./ProductState";
import { ReturnCondition } from "../../Api/Models/return.model";
import { ExchangeLifecycleIcon } from "../Icons/ExchangeLifecycleIcon";
import { ExchangeAccidentIcon } from "../Icons/ExchangeAccidentIcon";
import { ExchangeExceptionIcon } from "../Icons/ExchangeExceptionIcon";
import { ExchangeSizeIcon } from "../Icons/ExchangeSizeIcon";
import { CheckIcon } from "../Icons/CheckIcon";
import { LongArrowLeftIcon } from "../Icons/LongArrowLeftIcon";
import { v4 as uuidv4 } from 'uuid';
import Popover from "../Popover";


interface ExchangeProps {
  gears: RiderProductModel[];
  rider: RiderModel;
  updateGears: CallableFunction;
  missingKitProducts: KitModel | null;
  fetchMissingKitProducts: CallableFunction;
}

export interface ExceptionItem {
  gearId: number;
  variationId: number | null;
  replacementProductId?: number | null;
  condition: ReturnCondition;
  image: File;
  note: string | null;
}

export interface MultiExchangeItem {
  gearId: number;
  variationId: number | null;
  groupSelectedProductId?: null | number;
  groupSelectedProductVariationId?: null | number;
  replacementProductId?: number | null;
}

export interface TshirtItem {
  productId: number;
  variationId: number;
  quantity: number;
}

const Exchange: React.FunctionComponent<ExchangeProps> = ({
                                                            gears,
                                                            rider,
                                                            updateGears,
                                                            missingKitProducts,
                                                            fetchMissingKitProducts,
                                                          }) => {
  const { startLoading, finnishLoading } = useLoading();
  const [images, setImages] = useState<File[]>([]);
  const { showInfo } = useInfoModal();
  const [stocks, setStocks] = useState<StockModel[]>([]);

  //const navigate = useNavigate();
  const { setSubTitle, subTitle } =
      useOutletContext<RiderProfileLayoutContextInterface>();
  const [maxTshirtsCount, setMaxTshirtsCount] = useState<{
    maxTshirts: number;
    returnModifier: number;
  }>({ maxTshirts: 0, returnModifier: 0 });
  const [selectedGears, setSelectedGears] = useState<MultiExchangeItem[]>([]);
  const [returnedTshirts, setReturnedTshirts] = useState<TshirtItem[]>([]);
  const [dispatchedTshirts, setDispatchedTshirts] = useState<TshirtItem[]>([]);
  const [missingProducts, setMissingProducts] = useState<KitProductModel[]>(
      missingKitProducts?.products.filter((item: KitProductModel) => {
        return !gears.some((gear) => {
          return gear.replacementProduct?.some(
              (product) => product.id === item.productId
          );
        });
      }) ?? []
  );
  const [selectedExceptionGearId, setSelectedExceptionGearId] = useState<
      number | null
  >(null);
  const [tShirts, setTShirts] = useState<ProductModel[]>([]);

  const [exceptions, setExceptions] = useState<ExceptionItem[]>([]);

  useEffect(() => {
    let currnetSubTitle = subTitle;
    setSubTitle("");
    fetchStocks();
    fetchMaxTshirts();
    fetchTshirts();
    return () => {
      setSubTitle(currnetSubTitle);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (missingKitProducts?.products) {
      setMissingProducts(
          missingKitProducts.products.filter((item: KitProductModel) => {
            return !gears.some((gear) =>
                gear.replacementProduct?.some(
                    (product) => product.id === item.productId
                )
            );
          })
      );
    }
  }, [missingKitProducts?.products, gears]);

  const fetchStocks = () => {
    startLoading("get-stock-list");
    stockService
        .listStocks()
        .then((stocks) => {
          setStocks(stocks);
        })
        .finally(() => {
          finnishLoading("get-stock-list");
        });
  };

  const fetchMaxTshirts = () => {
    riderService.getAssignTshirtsCount(rider.riderId).then((response) => {
      setMaxTshirtsCount(response);
    });
  };

  const fetchTshirts = () => {
    startLoading("exchange-fetch-tshirts");
    riderService
        .listTshirts(rider.riderId)
        .then((products) => {
          setTShirts(
              products.map((item) => {
                item.selectedVariationId = item.variations[0].id;
                return item;
              })
          );
        })
        .finally(() => {
          finnishLoading("exchange-fetch-tshirts");
        });
  };

  const enrichedGears = selectedGears.map((item) => {

    return {
      ...item,
      replacementProductId: item.replacementProductId ?? null,
    };
  });

  const stockAvailable = (
      gear: RiderProductModel,
      exchangeProductId?: number | null
  ) => {
    let productStocks: StockModel[] = [];

    if (
        exchangeProductId &&
        gear.groupProducts?.find((x) => x.id === exchangeProductId)
    ) {
      productStocks = stocks.filter((x) => x.productId === exchangeProductId);
      let groupProduct = gear.groupProducts.find(
          (x) => x.id === exchangeProductId
      );

      if ((groupProduct?.variations.length ?? 0) > 0) {
        let stockAvailable = false;
        groupProduct?.variations.forEach((variation) => {
          if (
              (productStocks.find((x) => x.variationId === variation.id)
                  ?.quantity ?? -1) > 0
          ) {
            stockAvailable = true;
          }
        });

        return stockAvailable;
      } else if (productStocks.length === 1) {
        return productStocks[0].quantity > 0;
      }
    }

    if (gear.replacementProduct && gear.replacementProduct.length > 0) {
      let stockAvailable = false;

      gear.replacementProduct.forEach((product) => {
        const productStocks = stocks.filter((x) => x.productId === product.id);

        if (product.variations.length > 0) {
          product.variations.forEach((variation) => {
            const stock = productStocks.find(
                (x) => x.productId === product.id && x.variationId === variation.id
            );
            if ((stock?.quantity ?? 0) > 0) {
              stockAvailable = true;
            }
          });
        } else {
          const stock = productStocks.find(
              (x) => x.productId === product.id && x.variationId === null
          );
          if ((stock?.quantity ?? 0) > 0) {
            stockAvailable = true;
          }
        }
      });

      return stockAvailable;
    }


    productStocks = stocks.filter((x) => x.productId === gear.productId);

    if (gear.availableVariations.length > 0) {
      let stockAvailable = false;
      gear.availableVariations.forEach((variation) => {
        if (
            (productStocks.find((x) => x.variationId === variation.id)
                ?.quantity ?? -1) > 0
        ) {
          stockAvailable = true;
        }
      });

      return stockAvailable;
    }

    return (
        (productStocks.find((x) => x.variationId === null)?.quantity ?? -1) > 0
    );
  };

  const exchangeMultiple = (e: React.FormEvent) => {
    e.preventDefault();

    if (
        selectedGears.length < 1 &&
        returnedTshirts.length < 1 &&
        dispatchedTshirts.length < 1 &&
        missingProducts.filter((x) => x.checked).length < 1 &&
        exceptions.length < 1
    ) {
      return;
    }

    if (
        (selectedGears.some((x) =>
                gears
                    .filter((y) => y.exchangeList !== "size_change")
                    .some((y) => y.id === x.gearId)
            ) ||
            returnedTshirts.length > 0 ||
            dispatchedTshirts.length > 0 ||
            missingProducts.filter((x) => x.checked).length > 0 ||
            exceptions.length > 0) &&
        images.length < 1
    ) {
      return;
    }

    if (
        selectedGears.some((x) => x.variationId === -1) ||
        exceptions.some((x) => x.variationId === -1)
    ) {
      showInfo(t("Please select a size for all exchange products!"));
      return;
    }

    startLoading("profile-multiple-exchange");
    riderService
        .multiExchange(
            rider.riderId,
            enrichedGears,
            returnedTshirts,
            dispatchedTshirts,
            missingProducts.filter((x) => x.checked),
            exceptions,
            images[0]
        )
        .then(() =>
        {
          setSelectedGears([]);
          setReturnedTshirts([]);
          setDispatchedTshirts([]);
          setImages([]);
          updateGears();
          fetchStocks();
          setMissingProducts([]);
          fetchMissingKitProducts();
          setExceptions([]);
          setMaxTshirtsCount({ maxTshirts: 0, returnModifier: 0 });
          fetchMaxTshirts();
          showInfo(t("Items successfully exchanged!"));
        })
        .catch((reason) => {
          console.log(reason);
          if (reason.message === "This request has already been processed.") {
            showInfo(t("This request has already been processed."));
          }
          else
          {
            showInfo(t("Items exchange was unsuccessfull!"));
          }
        })
        .finally(() => {
          finnishLoading("profile-multiple-exchange");
        });
  };

  useEffect(() => {
    const newRequestId = uuidv4();
    localStorage.setItem('multiExchangeRequestId', newRequestId);
    console.log('newRequestId', newRequestId);
  }, []);

  // Updated getExchangeListItem with auto-check, auto-select first available variation, hide select, and "<< Select another product"

  const getExchangeListItem = (gear: RiderProductModel) => {
    const selectedGear = selectedGears.find((x) => x.gearId === gear.id);
    const selectedReplacement = gear.replacementProduct?.find(
        (p) => p.id === selectedGear?.replacementProductId
    );

    const handleReplacementChange = (productId: number) => {
      const replacement = gear.replacementProduct?.find((p) => p.id === productId);

      let firstAvailableVariationId: number | null = null;
      if (replacement?.variations?.length) {
        const firstAvailable = replacement.variations.find((v) =>
            isInStock(replacement.id, v.id)
        );
        firstAvailableVariationId = firstAvailable?.id ?? -1;
      }

      setSelectedGears((prev) => [
        ...prev.filter((x) => x.gearId !== gear.id),
        {
          gearId: gear.id,
          replacementProductId: productId,
          variationId: firstAvailableVariationId,
        },
      ]);
    };

    const handleVariationChange = (variationId: number) => {
      setSelectedGears((prev) => {
        const existing = prev.find((item) => item.gearId === gear.id);

        if (existing) {
          return prev.map((item) =>
              item.gearId === gear.id ? { ...item, variationId } : item
          );
        } else {
          return [
            ...prev,
            {
              gearId: gear.id,
              variationId,
              replacementProductId: hasReplacement ? gear.replacementProduct?.[0]?.id ?? null : null,
            },
          ];
        }
      });
    };

    const resetReplacement = () => {
      setSelectedGears((prev) => prev.filter((x) => x.gearId !== gear.id));
    };

    const isInStock = (productId: number, variationId: number | null) => {
      const stock = stocks.find(
          (x) => x.productId === productId && x.variationId === variationId
      );
      return (stock?.quantity ?? 0) > 0;
    };

    const hasReplacement =
        Array.isArray(gear.replacementProduct) &&
        gear.replacementProduct.length > 0;
    const isReplacementSelected = selectedReplacement !== undefined;

    const isOwnOutOfStock = !hasReplacement && !stockAvailable(gear);



    return (
        <div
            className={`product-list-card mb-4 ${selectedGear ? "marked" : ""}`}
            key={gear.id}
        >
          <div className="relative">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="product-list-card__image">
                  <img
                      src={
                        isReplacementSelected
                            ? selectedReplacement?.image ?? "https://via.placeholder.com/60"
                            : gear.productImage ?? "https://via.placeholder.com/60"
                      }
                      alt="product"
                  />
                </div>

                {/*{gear.availableVariations.length > 0 && (
                    <div className="product-list-card__variation-name">
                      {gear.availableVariations.find((v) => v.id === gear.productVariationId)?.name || "N/A"}
                    </div>
                )}*/}
              </div>

              <div className="w-full">
                <div className="product-list-card__title">
                  {hasReplacement
                      ? selectedReplacement?.name ?? ""
                      : gear.productName}
                </div>
                {!hasReplacement && !stockAvailable(gear) && (
                    <span className="text-red">{t("Out of stock")}</span>
                )}

                  {hasReplacement && !isReplacementSelected && (
                      <select
                          className={`input-field input-field--small input-field--auto-width ${!isReplacementSelected ? "text-gray" : "text-blue"}`}
                          value={-1}
                          onChange={(e) => handleReplacementChange(parseInt(e.target.value))}
                      >
                        <option value={-1}>- Please Select -</option>
                        {Array.isArray(gear.replacementProduct) &&
                            gear.replacementProduct
                                .filter((p) =>
                                    p.variations.length > 0
                                        ? p.variations.some((v) => isInStock(p.id, v.id))
                                        : isInStock(p.id, null)
                                )
                                .map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.name}
                                    </option>
                                ))}
                      </select>
                  )}

                  {hasReplacement && isReplacementSelected && (
                      <span className="text-blue" onClick={resetReplacement}>
                  {t("<< Select an other product")}
                </span>
                  )}
              </div>

              <div className="flex gap-2 items-center">
                {isReplacementSelected && selectedReplacement?.variations.length > 0 && (
                    <select
                        className="input-field input-field--small input-field--variation-width"
                        value={selectedGear?.variationId ?? -1}
                        onChange={(e) => handleVariationChange(parseInt(e.target.value))}
                    >
                      <option value={-1}>{t("Select size")}</option>
                      {selectedReplacement.variations
                          .filter((v) => isInStock(selectedReplacement.id, v.id))
                          .map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.name}
                              </option>
                          ))}
                    </select>
                )}

                {!hasReplacement && gear.availableVariations.length > 0 && !isOwnOutOfStock && (
                    <select
                        className="input-field input-field--small input-field--variation-width"
                        value={selectedGear?.variationId ?? -1}
                        onChange={(e) => handleVariationChange(parseInt(e.target.value))}
                    >
                      <option value={-1}>{t("Select size")}</option>
                      {gear.availableVariations
                          .filter((v) => isInStock(gear.productId, v.id))
                          .map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.name}
                              </option>
                          ))}
                    </select>
                )}


                {!isOwnOutOfStock && (
                    <button
                        type="button"
                        className={
                          selectedGear
                              ? "product-list-card__check checked"
                              : "product-list-card__check needtocheck"
                        }
                        onClick={() => {
                          if (selectedGear) {
                            setSelectedGears((prev) => prev.filter((x) => x.gearId !== gear.id));
                          } else {
                            const existingGear = selectedGears.find((x) => x.gearId === gear.id);

                            const defaultVariationId =
                                existingGear?.variationId !== undefined
                                    ? existingGear.variationId
                                    : hasReplacement && selectedReplacement?.variations.length
                                        ? selectedReplacement.variations.find((v) =>
                                        isInStock(selectedReplacement.id, v.id)
                                    )?.id ?? -1
                                        : gear.availableVariations.length > 0
                                            ? gear.availableVariations.find((v) =>
                                            isInStock(gear.productId, v.id)
                                        )?.id ?? -1
                                            : null;

                            setSelectedGears((prev) => [
                              ...prev.filter((x) => x.gearId !== gear.id),
                              {
                                gearId: gear.id,
                                variationId: defaultVariationId,
                                replacementProductId:
                                    gear.exchangeList === "size_change"
                                        ? gear.productId
                                        : isReplacementSelected
                                            ? selectedReplacement?.id ?? null
                                            : null,
                              },
                            ]);
                          }
                        }}
                    >
                      {selectedGear && <CheckIcon />}
                    </button>
                )}
              </div>

              <div className="exchange-list-card__exchange-type-icon-holder">
                {gear.exchangeList === "size_change" ? (
                    <Popover defaultPosition={"left"} text={"Size change"}>
                      <div className="exchange-list-card__exchange-type-icon">
                        <ExchangeSizeIcon/>
                      </div>
                    </Popover>
                ) : gear.exchangeList === "accident" ? (
                    <Popover defaultPosition={"left"} text={t('Accident')}>
                      <div className="exchange-list-card__exchange-type-icon">
                        <ExchangeAccidentIcon/>
                      </div>
                    </Popover>
                ) : (
                    <Popover defaultPosition={"left"} text={"Eligible exchange"}>
                      <div className="exchange-list-card__exchange-type-icon">
                        <ExchangeLifecycleIcon/>
                      </div>
                    </Popover>
                )}
              </div>
            </div>
          </div>

          {/*<div className="text-secondary mt-2">
            {(isReplacementSelected
                    ? selectedReplacement?.variations
                    : gear.availableVariations
            )?.find((v) => v.id === selectedGear?.variationId)?.name || ""}
          </div>*/}
        </div>
    );
  };


  const addExceptionData = (
      condition: ReturnCondition,
      image: File,
      note: string | null
  ) => {
    if (!selectedExceptionGearId) return;

    const existing = exceptions.find((x) => x.gearId === selectedExceptionGearId);

    setExceptions((prev) => [
      ...prev.filter((x) => x.gearId !== selectedExceptionGearId), // csak egy legyen
      {
        gearId: selectedExceptionGearId,
        variationId:
            (gears.find((x) => x.id === selectedExceptionGearId)?.availableVariations?.length ?? 0) > 0
                ? -1
                : null,
        replacementProductId: existing?.replacementProductId ?? null,
        condition,
        image,
        note,
      },
    ]);

    setSelectedExceptionGearId(null);
  };


  if (stocks.length === 0) {
    return null;
  }

  if (selectedExceptionGearId) {



    //return (
    //    <ProductState
    //        saveButtonOnClick={addExceptionData}
    //        backButtonOnClick={() => setSelectedExceptionGearId(null)}
    //    />
    //);

    const selectedExceptionGear = gears.find((gear) => gear.id === selectedExceptionGearId);

    return (
        <div
            className={"modal" + (!selectedExceptionGearId ? " hidden" : "")}
            tabIndex={-1}
            aria-modal="true"
            role="dialog"
        >
          <div className="modal__dialog modal-dialog--full-height">
            <div className="modal__body">

              <div className="navigationbar navigationbar--dark mb-6">
                <div className="navigationbar__left">
                <span
                    onClick={() => {
                      setExceptions((prev) => prev.filter((x) => x.gearId !== selectedExceptionGearId));
                      setSelectedExceptionGearId(null);
                    }}
                    className="navigationbar__button">
                  <LongArrowLeftIcon />
                </span>
                </div>
                <div className="navigationbar__center">
                  <div className="navigationbar__title">{t("Exception")}</div>
                </div>
                <div className="navigationbar__right">
                </div>
              </div>

              <div className="product-list-card mb-6" key={selectedExceptionGear!.id}>
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <div className="product-list-card__image">
                        <img src={selectedExceptionGear!.productImage} alt="package name"/>
                      </div>

                      {selectedExceptionGear!.availableVariations.length > 0 && (
                          <div className="product-list-card__variation-name">
                            {selectedExceptionGear!.availableVariations.find((v) => v.id === selectedExceptionGear!.productVariationId)?.name || "N/A"}
                          </div>
                      )}
                    </div>

                    <div className="w-full">
                      <div className="product-list-card__title">
                        {selectedExceptionGear!.productName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="heading mb-3">{t("Type of the problem")}</div>

              <ProductState
                  saveButtonOnClick={addExceptionData}
                  backButtonOnClick={() => {
                    setExceptions((prev) => prev.filter((x) => x.gearId !== selectedExceptionGearId));
                    setSelectedExceptionGearId(null);
                  }}
                  initialData={
                      exceptions.find((x) => x.gearId === selectedExceptionGearId) || {
                        condition: null,
                        image: null,
                        note: null,
                      }
                  }
              />
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="">
        <form onSubmit={exchangeMultiple}>


          {/*{gears.some((x) => x.exchangeList === "eligible") ? (
              <h1 className="heading mb-2">
                {t("Eligible exchange")}
              </h1>
          ) : null} */}

          {gears
              .filter((x) => x.exchangeList === "eligible" && !x.isTshirt)
              .map((gear) => {
                return getExchangeListItem(gear);
              })}

          <ExchangeTshirts
              dispatchedTshirts={dispatchedTshirts}
              returnedTshirts={returnedTshirts}
              gears={gears}
              setDispatchedTshirts={setDispatchedTshirts}
              setReturnedTshirts={setReturnedTshirts}
              stocks={stocks}
              maxTshirtsCount={maxTshirtsCount}
              tShirts={tShirts}
              setTShirts={setTShirts}
          />

          {gears.some((x) => x.exchangeList === "accident") ? (
              <h1 className="heading mb-2">
                {/* {t("Accidents")} */}
              </h1>
          ) : null}

          {gears
              .filter((x) => x.exchangeList === "accident")
              .map((gear) => {
                return getExchangeListItem(gear);
              })}

          {missingProducts.length > 0 ? (
              <>
                {/*<h1 className="heading mb-2">
                  {t("Missing Products")}
                </h1>*/}
                <ExchangeMissing
                    missingProducts={missingProducts}
                    setMissingProducts={setMissingProducts}
                    stocks={stocks}
                />
              </>
          ) : null}

          {/*{gears.some((x) => x.exchangeList === "size_change") ? (
              <h1 className="heading mb-2">
                {t("Size changes")}
              </h1>
          ) : null}*/}
          {gears
              .filter((x) => x.exchangeList === "size_change")
              .map((gear) => {
                return getExchangeListItem(gear);
              })}

          {/*{gears.some((x) => x.exchangeList === "exception") ? (
              <h1 className="heading mb-2">
                {t("Exceptions")}
              </h1>
          ) : null}*/}

          {gears
              .filter((x) => x.exchangeList === "exception")
              .map((gear) => {
                const exception = exceptions.find((x) => x.gearId === gear.id);
                const selectedReplacement = gear.replacementProduct?.find(
                    (p) => p.id === exception?.replacementProductId
                );

                const isInStock = (productId: number, variationId: number | null) => {
                  return (
                      (stocks.find(
                          (s) => s.productId === productId && s.variationId === variationId
                      )?.quantity ?? 0) > 0
                  );
                };

                const hasReplacement = Array.isArray(gear.replacementProduct) && gear.replacementProduct.length > 0;
                const isReplacementSelected = !!selectedReplacement;

                return (
                    <div
                        className={`product-list-card mb-4 ${
                            exception ? "marked" : ""
                        }`}
                        key={gear.id}
                    >
                      <div className="relative">
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <div className="product-list-card__image">
                              <img
                                  src={
                                      selectedReplacement?.image ??
                                      gear.productImage ??
                                      "https://via.placeholder.com/60"
                                  }
                                  alt="product"
                              />
                            </div>
                          </div>

                          <div className="w-full">
                            <div className="product-list-card__title">
                              {isReplacementSelected
                                  ? selectedReplacement?.name
                                  : gear.productName}
                            </div>
                            <div className="exchange-list-card__exchange-type-icon-holder">
                              <Popover defaultPosition={"left"} text={"Exception"}>
                                <div className="exchange-list-card__exchange-type-icon">
                                  <ExchangeExceptionIcon />
                                </div>
                              </Popover>
                            </div>
                            {/* REPLACEMENT SELECT */}
                            {hasReplacement && !isReplacementSelected && (
                                <select
                                    className="input-field input-field--small input-field--auto-width"
                                    value={-1}
                                    onChange={(e) => {
                                      const productId = parseInt(e.target.value);
                                      const product = gear.replacementProduct?.find(
                                          (p) => p.id === productId
                                      );
                                      const firstAvailable = product?.variations.find((v) =>
                                          isInStock(product.id, v.id)
                                      );
                                      const variationId = firstAvailable?.id ?? -1;

                                      setExceptions((prev) =>
                                          prev.map((item) =>
                                              item.gearId === gear.id
                                                  ? {
                                                    ...item,
                                                    replacementProductId: productId,
                                                    variationId,
                                                  }
                                                  : item
                                          )
                                      );
                                    }}
                                    disabled={!exception}
                                >
                                  <option value={-1}>- {t("Select replacement product")} -</option>
                                  {Array.isArray(gear.replacementProduct) &&
                                      gear.replacementProduct
                                          .filter((p) =>
                                              p.variations.length > 0
                                                  ? p.variations.some((v) =>
                                                      isInStock(p.id, v.id)
                                                  )
                                                  : isInStock(p.id, null)
                                          )
                                          .map((p) => (
                                              <option key={p.id} value={p.id}>
                                                {p.name}
                                              </option>
                                          ))}
                                </select>
                            )}

                            {hasReplacement && isReplacementSelected && (
                                <span
                                    className="text-blue cursor-pointer"
                                    onClick={() => {
                                      setExceptions((prev) =>
                                          prev.map((item) =>
                                              item.gearId === gear.id
                                                  ? {
                                                    ...item,
                                                    replacementProductId: null,
                                                    variationId: -1,
                                                  }
                                                  : item
                                          )
                                      );
                                    }}
                                >
                  {t("<< Select an other product")}
                </span>
                            )}
                          </div>

                          {/* VARIATION SELECT */}
                          <div className="flex gap-2 items-center">
                            {isReplacementSelected &&
                                selectedReplacement?.variations.length > 0 && (
                                    <select
                                        className="input-field input-field--small input-field--variation-width"
                                        onChange={(e) => {
                                          const newVarId = parseInt(e.target.value);
                                          setExceptions((prev) =>
                                              prev.map((item) =>
                                                  item.gearId === gear.id
                                                      ? { ...item, variationId: newVarId }
                                                      : item
                                              )
                                          );
                                        }}
                                        value={exception?.variationId ?? -1}
                                        disabled={!exception}
                                    >
                                      <option value={-1}>{t("Select size")}</option>
                                      {selectedReplacement.variations
                                          .filter((v) =>
                                              isInStock(selectedReplacement.id, v.id)
                                          )
                                          .map((v) => (
                                              <option key={v.id} value={v.id}>
                                                {v.name}
                                              </option>
                                          ))}
                                    </select>
                                )}

                            {!hasReplacement &&
                                gear.availableVariations.length > 0 && (
                                    <select
                                        className="input-field input-field--small input-field--variation-width"
                                        onChange={(e) => {
                                          const varId = parseInt(e.target.value);
                                          setExceptions((prev) =>
                                              prev.map((item) =>
                                                  item.gearId === gear.id
                                                      ? { ...item, variationId: varId }
                                                      : item
                                              )
                                          );
                                        }}
                                        value={exception?.variationId ?? -1}
                                        disabled={!exception}
                                    >
                                      <option value={-1}>{t("Select size")}</option>
                                      {gear.availableVariations
                                          .filter((v) => isInStock(gear.productId, v.id))
                                          .map((v) => (
                                              <option key={v.id} value={v.id}>
                                                {v.name}
                                              </option>
                                          ))}
                                    </select>
                                )}

                            {/* OPEN MODAL */}
                            <div
                                className="icon-button icon-button--square icon-button--purple"
                                onClick={() => {
                                  if (!gear.exchangedDisabled && !gear.discontinued) {
                                    setSelectedExceptionGearId(gear.id);
                                  }
                                }}
                            >
                              <div
                                  className={
                                      "status " + (exception ? "closed" : "open")
                                  }
                              >
                                {exception ? <CheckIcon /> : <ExchangeLifecycleIcon />}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                );
              })}



          {selectedGears.length > 0 ||
          returnedTshirts.length > 0 ||
          dispatchedTshirts.length > 0 ||
          missingProducts.filter((x) => x.checked).length > 0 ||
          exceptions.length > 0 ? (
              <>
                <div className="mt-4 mb-4">
                  <ImageInput
                      images={images}
                      setImages={setImages}
                      required={
                          selectedGears.some((x) =>
                              gears
                                  .filter((y) => y.exchangeList !== "size_change")
                                  .some((y) => y.id === x.gearId)
                          ) ||
                          returnedTshirts.length > 0 ||
                          dispatchedTshirts.length > 0 ||
                          missingProducts.filter((x) => x.checked).length > 0 ||
                          exceptions.length > 0
                      }
                      infoText={t("Confirmation photo")}
                  />
                </div>
                <button
                    type="submit"
                    className="button  w-full"
                >
                  {t("Exchange")} / {t("Add")}
                </button>
              </>
          ) : null}
        </form>
      </div>
  );
};

export default Exchange;